import React, { createContext, useContext, useEffect, useState } from "react";
import { useFirestore, useFunctions } from "reactfire";
import Year from "../../database/models/Year";
import UserNotification from "../../shared/components/Notification/UserNotification";
import {
  ApplicationStatus,
  Country,
  TeamRole,
} from "../../shared/constants/Constant";
import { getCollectionNameByCountry } from "../../shared/utils/Commons";
import { useAppUser } from "../user-context/UserContext";
import { useYears } from "../year-context/YearContext";

export enum MApplicationStatus {
  STATUS = "",
  ALL = "ALL",
  PENDING = "PENDING",
  ACCEPTED = "ACCEPTED",
  REJECTED = "REJECTED",
}

export enum MApplicationCountry {
  SELECT_COUNTRY = "",
  ALL = "ALL",
  MALAYSIA = "MALAYSIA",
  SRI_LANKA = "SRI_LANKA",
  BOSNIA = "BOSNIA",
  JAMAICA = "JAMAICA",
  INDIA = "INDIA",
  INDONESIA = "INDONESIA",
}

const ITEM_COUNT_IN_PAGE: number = 10;

export interface IApplicationsContext {
  applications: ApplicationWithId[];
  selectedApplicationId: string;
  setSelectedApplicationId: (id: string) => void;
  selectedApplication?: ApplicationWithId;
  filters: Filter;
  loading: boolean;
  setFilters: (filters: Filter, initial?: boolean) => void;
  orders: Orders;
  setOrders: (orders: Orders) => void;
  pageNumber: number;
  totalPages: number;
  setPageNumber: (pageNumber: number) => void;
  acceptApplication: (
    application: ApplicationWithId,
    onFinished: () => void
  ) => void;
  rejectApplication: (
    application: ApplicationWithId,
    onFinished: () => void
  ) => void;
  createAccounts: (
    application: ApplicationWithId,
    onFinished: () => void
  ) => void;
  years: Year[];
  setYears: (years: Year[]) => void;
}

const initialContext: IApplicationsContext = {
  applications: [],
  selectedApplicationId: "",
  selectedApplication: undefined,
  setSelectedApplicationId: (id: string) => {},
  filters: {
    status: MApplicationStatus.STATUS,
    teamName: "",
    country: MApplicationCountry.SELECT_COUNTRY,
    year: 2020,
  },
  loading: false,
  setFilters: (filters: Filter, initial: boolean = false) => {},
  orders: {
    appliedDate: "DESCENDING",
    teamName: "ASCENDING",
    profile: "ASCENDING",
    current: "TEAM_NAME",
  },
  setOrders: (orders: Orders) => {},
  pageNumber: 1,
  totalPages: 0,
  setPageNumber: (pageNumber: number) => {},
  acceptApplication: (application: ApplicationWithId) => {},
  rejectApplication: (application: ApplicationWithId) => {},
  createAccounts: (application: ApplicationWithId) => {},
  years: [],
  setYears: (years: Year[]) => {},
};

const ApplicationsContext = createContext<IApplicationsContext>(initialContext);

interface IProps {
  children: any;
}

export const ApplicationsProvider = (props: IProps) => {
  const initialFilters: Filter = {
    status: MApplicationStatus.STATUS,
    teamName: "",
    country: MApplicationCountry.SELECT_COUNTRY,
    year: 2020,
  };
  const initialOrders: Orders = {
    appliedDate: "DESCENDING",
    teamName: "ASCENDING",
    profile: "ASCENDING",
    current: "TEAM_NAME",
  };

  const initialYearState: Year[] = [];
  const initialApplications: ApplicationWithId[] = [];
  const initialPagination = {
    total_pages: 0,
    page_number: 0,
  };

  const [filterState, setFilterState] = useState(initialFilters);
  const [orderState, setOrderState] = useState(initialOrders);
  const [FApplications, setFApplications] = useState<ApplicationWithId[]>([]);
  const [selectedApplicationId, setSelectedApplicationIdState] = useState<
    string
  >("");
  const [selectedApplication, setSelectedApplicationState] = useState<
    ApplicationWithId | undefined
  >();
  const [applications, setApplications] = useState(initialApplications);
  const [pagination, setPagination] = useState(initialPagination);

  const [loading, setLoading] = useState(false);

  const store = useFirestore();
  const appUser = useAppUser();
  const yearsObject = useYears();
  const [years, setYearsState] = useState(initialYearState);

  const fChangeStatus = useFunctions().httpsCallable("updateApplicationStatus");
  const fGenTeams = useFunctions().httpsCallable("generateTeam");

  useEffect(() => {
    setYearsState(() => {
      const yearsArr: Year[] = Object.assign([], [yearsObject][0]);
      const sorted: Year[] = yearsArr
        ? yearsArr.sort((a, b) => (a.appliedYear > b.appliedYear ? 1 : -1))
        : [];

      if (sorted.length > 0) {
        setFilterState((prevState) => {
          prevState.year = sorted[sorted.length - 1].appliedYear;

          return prevState;
        });
      }

      return sorted;
    });
  }, [yearsObject]);

  useEffect(() => {
    setSelectedApplicationState((ps) => {
      const [newSelected] = applications.filter((ap) => ap.id === ps?.id);
      if (newSelected) {
        return newSelected;
      }
      return ps;
    });
  }, [applications]);

  /*
  If applications changed re-calculate pagenations
  */
  useEffect(() => {
    if (FApplications.length % ITEM_COUNT_IN_PAGE === 0) {
      const totalPages = FApplications.length / ITEM_COUNT_IN_PAGE;
      setPagination((ps) => ({ ...ps, total_pages: totalPages }));
    } else {
      const totalPages =
        Math.floor(FApplications.length / ITEM_COUNT_IN_PAGE) + 1;
      setPagination((ps) => ({ ...ps, total_pages: totalPages }));
    }
  }, [FApplications]);

  /*
  Get applications from firestore
  */
  useEffect(() => {
    setApplications([]);
    let countries: (Country | MApplicationCountry)[] = [appUser.country];
    if (
      appUser.userRole.isAdmin &&
      (filterState.country === MApplicationCountry.SELECT_COUNTRY ||
        filterState.country === MApplicationCountry.ALL)
    ) {
      countries = [
        Country.SRI_LANKA,
        Country.BOSNIA,
        Country.MALAYSIA,
        Country.JAMAICA,
        Country.INDIA,
        Country.INDONESIA,
      ];
    } else if (appUser.userRole.isAdmin) {
      countries = [filterState.country];
    }

    const unsubscribers: any[] = [];

    countries.forEach((country) => {
      unsubscribers.push(
        store
          .collection(getCollectionNameByCountry(country))
          .onSnapshot({}, (snapshot) => {
            snapshot.docChanges().forEach((change) => {
              if (change.type === "added") {
                setApplications((ps) => {
                  ps.push({
                    ...(change.doc.data() as any),
                    id: change.doc.id,
                    createdAt: change.doc.get("createdAt"),
                    updatedAt: change.doc.get("updatedAt"),
                  });
                  return Object.assign([], ps);
                });
              }
              if (change.type === "modified") {
                setApplications((ps) => {
                  const modified = ps.map((a) => {
                    if (a.id === change.doc.id) {
                      return {
                        ...(change.doc.data() as any),
                        id: change.doc.id,
                        createdAt: change.doc.get("createdAt"),
                        updatedAt: change.doc.get("updatedAt"),
                      };
                    } else {
                      return a;
                    }
                  });
                  return Object.assign([], modified);
                });
              }
              if (change.type === "removed") {
                setApplications((ps) => {
                  const removed = ps.filter((a) => a.id !== change.doc.id);
                  return Object.assign([], removed);
                });
              }
            });
          })
      );
    });

    return () => {
      unsubscribers.forEach((unsubscribe) => {
        unsubscribe();
      });
    };
  }, [
    store,
    appUser.country,
    appUser.userRole.isAdmin,
    appUser.userRole.isModerator,
    filterState.country,
  ]);

  /*
  Reset pagination if data filtered
  */
  useEffect(() => {
    setPagination((ps) => {
      return { ...ps, page_number: 0 };
    });
  }, [filterState.teamName, filterState.status]);

  /*
  Filtering and Ordering the applications
  */
  useEffect(() => {
    let c_applications = applications;
    switch (orderState.current) {
      case "APPLIED_DATE":
        if (orderState.appliedDate === "ASCENDING") {
          c_applications.sort(
            (a: any, b: any) => a.createdAt?.seconds - b.createdAt?.seconds
          );
        } else {
          c_applications.sort(
            (a: any, b: any) => b.createdAt?.seconds - a.createdAt?.seconds
          );
        }
        break;
      case "PROFILE":
        if (orderState.profile === "ASCENDING") {
          c_applications.sort((a: ApplicationWithId, b: ApplicationWithId) =>
            a.teamGenerated === b.teamGenerated ? 0 : a.teamGenerated ? -1 : 1
          );
        } else {
          c_applications.sort((a: ApplicationWithId, b: ApplicationWithId) =>
            a.teamGenerated === b.teamGenerated ? 0 : a.teamGenerated ? 1 : -1
          );
        }
        break;
      default:
        if (orderState.teamName === "ASCENDING") {
          c_applications.sort((a: ApplicationWithId, b: ApplicationWithId) => {
            if (a.teamName && b.teamName) {
              return b.teamName?.toLowerCase() > a.teamName?.toLowerCase()
                ? -1
                : 1;
            } else {
              return 0;
            }
          });
        } else {
          c_applications.sort((a: ApplicationWithId, b: ApplicationWithId) => {
            if (a.teamName && b.teamName) {
              return b.teamName?.toLowerCase() < a.teamName?.toLowerCase()
                ? -1
                : 1;
            } else {
              return 0;
            }
          });
        }
        break;
    }
    if (filterState.teamName.trim().length > 0) {
      c_applications = c_applications.filter((application) =>
        application.teamName
          ?.toLowerCase()
          .includes(filterState.teamName.toLowerCase())
      );
    }
    if (
      filterState.status !== MApplicationStatus.ALL &&
      filterState.status !== MApplicationStatus.STATUS
    ) {
      c_applications = c_applications.filter(
        (application) =>
          application.acceptanceState.toString() ===
          filterState.status.toString()
      );
    }
    if (filterState.year > 0 && years && years.length > 0) {
      const startsAt: Date = years.filter(
        (y) => y.appliedYear === filterState.year
      )[0]?.startsAt;
      const endsAt: Date = years.filter(
        (y) => y.appliedYear === filterState.year
      )[0]?.endsAt;

      c_applications = c_applications.filter((application) =>
        application.createdAt
          ? application.createdAt.toDate() >= startsAt &&
            application.createdAt.toDate() <= endsAt
          : false
      );
    }
    setFApplications((ps) => {
      return c_applications;
    });
  }, [
    filterState.teamName,
    filterState.status,
    filterState.year,
    years,
    applications,
    orderState,
  ]);

  const acceptApplication = (
    application: ApplicationWithId,
    onFinished: () => void
  ) => {
    setLoading(true);
    fChangeStatus({
      applicationID: application.id,
      country: application.country,
      status: ApplicationStatus.ACCEPTED,
    })
      .then((resp) => {
        UserNotification({
          isSuccess: true,
          message: "Application successfully accepted!",
        });
      })
      .catch((error) => {
        console.log(error);
        UserNotification({
          isSuccess: false,
          message:
            "Accepting application failed!\nPlease check your connection.",
        });
      })
      .finally(() => {
        setLoading(false);
        onFinished();
      });
  };

  const rejectApplication = (
    application: ApplicationWithId,
    onFinished: () => void
  ) => {
    setLoading(true);
    fChangeStatus({
      applicationID: application.id,
      country: application.country,
      remarks: application.remarks,
      status: ApplicationStatus.REJECTED,
    })
      .then((resp) => {
        UserNotification({
          isSuccess: true,
          message: "Application successfully rejected!",
        });
      })
      .catch((error) => {
        console.log(error);
        UserNotification({
          isSuccess: false,
          message:
            "Rejecting application failed!\nPlease check your connection.",
        });
      })
      .finally(() => {
        setLoading(false);
        onFinished();
      });
  };

  const createAccounts = (
    application: ApplicationWithId,
    onFinished: () => void
  ) => {
    setLoading(true);
    fGenTeams({
      applicationID: application.id,
      country: application.country,
    })
      .then((resp) => {
        UserNotification({
          isSuccess: true,
          message: "Profiles Successfully Generated!",
        });
      })
      .catch((error) => {
        console.log(error);
        UserNotification({
          isSuccess: false,
          message: "Generating profiles failed!\nPlease check your connection.",
        });
      })
      .finally(() => {
        setLoading(false);
        onFinished();
      });
  };

  const setFilters = (filter: Filter, initial: boolean = false) => {
    if (initial) {
      setFilterState(initialFilters);
    } else {
      setFilterState((ps) => ({ ...ps, ...filter }));
    }
  };

  const setOrders = (orders: Orders) => {
    setOrderState((ps) => ({ ...ps, ...orders }));
  };

  const setPageNumber = (pageNumber: number) => {
    setPagination((ps) => ({ ...ps, page_number: pageNumber }));
  };

  const setSelectedApplicationId = (id: string) => {
    setSelectedApplicationIdState(id);
    const [sApplication] = applications.filter((a) => a.id === id);
    setSelectedApplicationState(sApplication);
  };

  const setYears = (years: Year[]) => setYearsState((ps) => [...ps, ...years]);

  const paginate = (
    array: ApplicationWithId[],
    page_number: number,
    page_size: number
  ): ApplicationWithId[] => {
    if (array.length > 0) {
      return array.slice(
        page_number * page_size,
        (page_number + 1) * page_size
      );
    } else {
      return [];
    }
  };

  return (
    <ApplicationsContext.Provider
      value={{
        applications: paginate(
          FApplications,
          pagination.page_number,
          ITEM_COUNT_IN_PAGE
        ),
        selectedApplicationId,
        setSelectedApplicationId,
        selectedApplication,
        loading: loading,
        filters: filterState,
        orders: orderState,
        setFilters,
        setOrders,
        pageNumber: pagination.page_number,
        totalPages: pagination.total_pages,
        setPageNumber,
        acceptApplication,
        rejectApplication,
        createAccounts,
        years,
        setYears,
      }}
    >
      {props.children}
    </ApplicationsContext.Provider>
  );
};

export const useApplications = (): IApplicationsContext =>
  useContext(ApplicationsContext);

export interface CommonApplication {
  teamName?: string;
  projectName?: string;
  inslId?: string;
  sdg?: string[];
  projectTheme?: string[];
  industries?: string[];
  solvingProblem?: string;
  solvingApproach?: string;
  importance?: string;
  stage?:
    | "Ideation"
    | "Idea Validated and Market Analysed"
    | "Business Plan Formulation"
    | "MVP launched"
    | "Early Revenue"
    | "Growth and Expansions"
    | string;
  projectProgress?: string;
  pitchLink?: string;
  proposalLink?: any;
  members?: CommonMember[];
  fullName?: string;
  dob?: Date;
  email?: string;
  contact?: string;
  placeOfResidence?: string;
  whoYouAre?: string;
  whatYouAreStudying?: string;
  cvLink?: any;
  motivation?: string;
  commitment?: string;
  pastExperience?: string;
  //for india
  problem?: string;
  unique?: string;
  progress?: string;
  pitch?: string;
  requiredInvensment?: string;
  invesmentDone?: string;
  expectedTime?: string;
  anyPatent?: string;
  whyEntreprenur?: string;
  howLoongThinking?: string;
  strenghts?: string;
  anyCourse?: string;
  howStrong?: number;
  admireEntreprenur?: string;
  primaryExpectation?: string;
  otherPrimaryExpectation?: string;
  additionalEffort?: boolean;
  attendAllWorkshops?: boolean;
  payRequiredEnrolmentFee?: boolean;
  domain?: string;
  college?: string;
  mentorName?: string;
  mentorEmail?: string;
  mentorContact?: string;
  // end india
  createdAt?: firebase.firestore.Timestamp;
  updatedAt?: firebase.firestore.Timestamp;
  country: Country;
}

interface CommonMember {
  fullName: string;
  email: string;
  name?: string;
  phone?:string;
  yearOfStudy?: string;
  university: string;
  role: TeamRole;
  contact?: string;
  contactNumber?: string;
  state?: string;
  country: Country;
  resideProvince?: string;
  dob?: string;
  residence?: string;
  status?: string;
  cvLink?: string;
  commitment?: string;
  motivation?: string;
  pastExperience?: string;
  studying?: string;
}

export interface ApplicationResponse extends CommonApplication {
  acceptanceState: ApplicationStatus;
  remarks: string;
  teamGenerated: boolean;
}

export interface ApplicationWithId extends ApplicationResponse {
  id: string;
}

interface Orders {
  current: "APPLIED_DATE" | "TEAM_NAME" | "PROFILE";
  appliedDate: "ASCENDING" | "DESCENDING";
  teamName: "ASCENDING" | "DESCENDING";
  profile: "ASCENDING" | "DESCENDING";
}

interface Filter {
  teamName: string;
  status: MApplicationStatus;
  country: MApplicationCountry;
  year: number;
}
